import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Banner, Button, ckConfirm, DataTable, Dialog, exportRows, Field, Select, StatGrid, StatTile, Tag, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { CkColSet, CkDataColumn, CkMenuItem, CkSelection, CkTableView } from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type DealDetail, type DealDto, type DealsPage as DealsData, type DealStatus } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { PeriodBar } from "../PeriodBar";
import { periodText, type Period } from "../periods";
import { useShell } from "../shell/ShellContext";
import { useServerTable, type EnumColumn } from "../useServerTable";
import { DealDialog } from "./DealDialog";
import { DealSheet, PaymentDialog } from "./DealSheet";
import { CHANNEL_LABEL, copyText, DEAL_STATUS, errorMessage, fmtIls, fmtMoney, fmtNum, REGION_LABEL, todayIso, withEmpty } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import "./sales.css";

/** links (dashboard, commissions, the customer card) may carry these named filters — kept as extra params with a chip */
const LINK_KEYS = ["productId", "repId", "channel", "region", "paymentMethodKey", "status", "customerId"] as const;
const STATUSES: DealStatus[] = ["open", "paid", "cancelled"];

const shekels = (agorot: number | null | undefined) => (agorot == null ? null : agorot / 100);

/** a few at a time (no bulk endpoint for deals) */
async function runEach(ids: string[], fn: (id: string) => Promise<unknown>): Promise<{ ok: number; failed: number }> {
  const results: Array<PromiseSettledResult<unknown>> = [];
  for (let i = 0; i < ids.length; i += 5) results.push(...(await Promise.allSettled(ids.slice(i, i + 5).map(fn))));
  const ok = results.filter((r) => r.status === "fulfilled").length;
  return { ok, failed: results.length - ok };
}

/** bulk «שיוך לנציג» */
function AssignRepDialog({ ids, onClose, onDone }: { ids: string[] | null; onClose: () => void; onDone: () => void }) {
  const toast = useCkToast();
  const { repOptions } = useSalesMeta();
  const [repId, setRepId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setRepId(""), [ids]);
  const submit = async () => {
    if (!ids) return;
    setBusy(true);
    try {
      const r = await runEach(ids, (id) => salesApi.deals.update(id, { repId: repId || null }));
      if (r.failed) toast.error(`${fmtNum(r.ok)} עודכנו · ${fmtNum(r.failed)} נכשלו`);
      else toast.success(`${fmtNum(r.ok)} עסקאות עודכנו`);
      onClose();
      onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={Boolean(ids)}
      onClose={onClose}
      size="sm"
      icon="user"
      title={`שיוך לנציג — ${fmtNum(ids?.length ?? 0)} עסקאות`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={busy} onClick={() => void submit()}>
            {busy ? "מעדכן…" : "עדכון"}
          </Button>
        </>
      }
    >
      <Field label="נציג">
        <Select options={withEmpty("— ללא נציג —", repOptions)} value={repId} onChange={setRepId} aria-label="נציג" />
      </Field>
    </Dialog>
  );
}

function initialState(p: URLSearchParams): { period: Period; link: Record<string, string> } {
  const link: Record<string, string> = {};
  LINK_KEYS.forEach((k) => {
    const v = p.get(k);
    if (v) link[k] = v;
  });
  return { period: { from: p.get("from") ?? "", to: p.get("to") ?? "" }, link };
}

/**
 * /sales/deals — every deal with sums; /sales/collections — only deals with a balance, largest first.
 * Turbo DataTable anatomy: period bar (saved in views), stat tiles + footer totals that follow the filters,
 * typed columns with column filters, column sets, views, row actions, bulk actions, export.
 */
export function DealsPage({ mode = "deals" }: { mode?: "deals" | "collections" }) {
  const collections = mode === "collections";
  const base = collections ? "/sales/collections" : "/sales/deals";
  const { id } = useParams<{ id?: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useCkToast();
  const { allowed } = useShell();
  const canEdit = allowed("sales.edit");
  const canManage = allowed("sales.manage");
  const { label, optionsOf } = useSalesMeta();

  const [init] = useState(() => initialState(params));
  const [period, setPeriod] = useState<Period>(init.period);
  const [link, setLink] = useState<Record<string, string>>(init.link);
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<DealDto | null>(null);
  const [assigning, setAssigning] = useState<string[] | null>(null);
  const [clearKey, setClearKey] = useState(0);

  const enums = useMemo<Record<string, EnumColumn>>(
    () => ({
      status: { label: (v) => DEAL_STATUS[v as DealStatus]?.label ?? v, options: STATUSES.map((s) => ({ value: s, label: DEAL_STATUS[s].label })) },
      channel: { label: (v) => CHANNEL_LABEL[v as keyof typeof CHANNEL_LABEL] ?? v, options: Object.entries(CHANNEL_LABEL).map(([value, l]) => ({ value, label: l })) },
      paymentMethodKey: { label: (v) => label("sales_payment_method", v), options: optionsOf("sales_payment_method", { all: true }) },
      region: { label: (v) => REGION_LABEL[v] ?? v, options: Object.entries(REGION_LABEL).map(([value, l]) => ({ value, label: l })) },
    }),
    [label, optionsOf],
  );

  const extra = useMemo(() => {
    const e: Record<string, string> = { ...link };
    if (period.from) e.from = period.from;
    if (period.to) e.to = period.to;
    if (collections) {
      e.balance = "open";
      e.sort = "balance";
    }
    return e;
  }, [link, period, collections]);

  const table = useServerTable<DealDto, DealsData>({
    load: (p) => salesApi.deals.list(p),
    loadValues: (p) => salesApi.deals.columnValues(p),
    extra,
    enums,
    errorText: "לא הצלחנו לטעון את העסקאות",
  });
  const { query, setQuery, firstPage, data, reload } = table;

  // keep the period + link filters in the URL (a link / back button / the open deal keeps them)
  useEffect(() => {
    const next = new URLSearchParams();
    if (period.from) next.set("from", period.from);
    if (period.to) next.set("to", period.to);
    Object.entries(link).forEach(([k, v]) => next.set(k, v));
    if (next.toString() !== params.toString() && params.get("new") !== "1") setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, link]);

  useEffect(() => {
    if (params.get("new") === "1" && canEdit) {
      setCreating(true);
      const next = new URLSearchParams(params);
      next.delete("new");
      setParams(next, { replace: true });
    }
  }, [params, setParams, canEdit]);

  const changePeriod = (p: Period) => {
    setPeriod(p);
    firstPage();
  };
  const openDeal = (d: { id: string }) => navigate(`${base}/${d.id}${window.location.search}`);
  const closeDeal = () => navigate(`${base}${window.location.search}`);
  const saved = (d: DealDetail) => {
    reload();
    if (!collections) openDeal(d);
  };
  const afterBulk = () => {
    setClearKey((k) => k + 1);
    reload();
  };

  const columns = useMemo<Array<CkDataColumn<DealDto>>>(
    () => [
      { key: "dealOn", label: "תאריך", type: "date", sortable: true },
      {
        key: "customerName",
        label: "לקוח",
        type: "text",
        sortable: true,
        getValue: (r) => r.customerName ?? "",
        render: (r) => <b>{r.customerName || "—"}</b>,
      },
      { key: "customerPhone", label: "טלפון", type: "text", ltr: true, copy: true, onCopied: () => toast("הועתק"), getValue: (r) => r.customerPhone ?? "" },
      { key: "productName", label: "מוצר", type: "text", sortable: true },
      {
        key: "totalIlsAgorot",
        label: "סכום",
        type: "money",
        sortable: true,
        sum: true,
        getValue: (r) => shekels(r.totalIlsAgorot),
        render: (r) => (
          <span className="sl-cell2 sl-num">
            <b>{fmtIls(r.totalIlsAgorot)}</b>
            {r.currency !== "ILS" ? <small>{fmtMoney(r.listPriceAgorot - r.discountAgorot, r.currency)}</small> : null}
          </span>
        ),
      },
      { key: "paidAgorot", label: "שולם", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.paidAgorot), render: (r) => <span className="sl-num">{fmtIls(r.paidAgorot)}</span> },
      {
        key: "balanceAgorot",
        label: "יתרה",
        type: "money",
        sortable: true,
        sum: true,
        getValue: (r) => shekels(r.balanceAgorot),
        render: (r) => <span className={`sl-num${r.balanceAgorot > 0 ? " sl-danger" : ""}`}>{fmtIls(r.balanceAgorot)}</span>,
      },
      { key: "vatAgorot", label: "מע״מ", type: "money", sortable: true, sum: true, getValue: (r) => shekels(r.vatAgorot), render: (r) => <span className="sl-num">{fmtIls(r.vatAgorot)}</span> },
      { key: "discountAgorot", label: "הנחה", type: "money", sortable: true, getValue: (r) => shekels(r.discountAgorot), render: (r) => (r.discountAgorot ? <span className="sl-num">{fmtMoney(r.discountAgorot, r.currency)}</span> : "") },
      { key: "installments", label: "תשלומים", type: "number", sortable: true },
      { key: "paymentMethodKey", label: "צורת תשלום", type: "enum", sortable: true, getValue: (r) => label("sales_payment_method", r.paymentMethodKey) },
      { key: "channel", label: "ערוץ", type: "enum", sortable: true, getValue: (r) => CHANNEL_LABEL[r.channel] ?? r.channel },
      { key: "repName", label: "נציג", type: "enum", sortable: true, getValue: (r) => r.repName ?? "" },
      {
        key: "status",
        label: "סטטוס",
        type: "enum",
        sortable: true,
        getValue: (r) => DEAL_STATUS[r.status]?.label ?? r.status,
        render: (r) => <Tag tone={DEAL_STATUS[r.status]?.tone ?? "neutral"}>{DEAL_STATUS[r.status]?.label ?? r.status}</Tag>,
      },
      { key: "region", label: "אזור", type: "enum", sortable: true, getValue: (r) => REGION_LABEL[r.region] ?? r.region },
      { key: "currency", label: "מטבע", type: "enum", sortable: true },
      { key: "source", label: "מקור", type: "text", sortable: true, getValue: (r) => r.source ?? "" },
      { key: "notes", label: "הערות", type: "text", getValue: (r) => r.notes ?? "" },
      { key: "createdAt", label: "נוצרה", type: "date", sortable: true },
    ],
    [label, toast],
  );

  const colSets: CkColSet[] = [
    { key: "basic", label: "בסיסי", cols: ["dealOn", "customerName", "productName", "totalIlsAgorot", "paidAgorot", "balanceAgorot", "repName", "status"] },
    { key: "money", label: "כספים", cols: ["dealOn", "customerName", "totalIlsAgorot", "discountAgorot", "vatAgorot", "paidAgorot", "balanceAgorot", "installments", "paymentMethodKey", "currency"] },
    { key: "sale", label: "מכירה", cols: ["dealOn", "customerName", "productName", "channel", "repName", "region", "source"] },
    { key: "contact", label: "קשר", cols: ["customerName", "customerPhone", "productName", "balanceAgorot", "notes"] },
  ];
  const basic = colSets[0]!.cols;
  const hiddenByDefault = columns.map((c) => c.key).filter((k) => !basic.includes(k) && k !== "installments" && k !== "paymentMethodKey");

  const periodState = { from: period.from, to: period.to };
  const views: CkTableView[] = collections
    ? [
        { id: "balance", label: "כל היתרות — מהגדולה", state: { query: { search: "", filters: { balanceAgorot: { mode: "cond", op: "gt", v1: "0" } }, sort: { key: "balanceAgorot", dir: -1 } }, extra: periodState } },
        { id: "oldest", label: "יתרות ישנות קודם", state: { query: { search: "", filters: { balanceAgorot: { mode: "cond", op: "gt", v1: "0" } }, sort: { key: "dealOn", dir: 1 } }, extra: periodState } },
        { id: "big", label: "יתרה מעל ₪1,000", state: { query: { search: "", filters: { balanceAgorot: { mode: "cond", op: "gt", v1: "1000" } }, sort: { key: "balanceAgorot", dir: -1 } }, extra: periodState } },
      ]
    : [
        { id: "all", label: "כל העסקאות", state: { query: { search: "", filters: {}, sort: null }, extra: periodState } },
        { id: "open", label: "עם יתרה", state: { query: { search: "", filters: { balanceAgorot: { mode: "cond", op: "gt", v1: "0" } }, sort: { key: "balanceAgorot", dir: -1 } }, extra: periodState } },
        { id: "big", label: "הגדולות", state: { query: { search: "", filters: {}, sort: { key: "totalIlsAgorot", dir: -1 } }, extra: periodState } },
        { id: "cancelled", label: "מבוטלות", state: { query: { search: "", filters: { status: { mode: "values", values: [DEAL_STATUS.cancelled.label] } }, sort: null }, extra: periodState } },
      ];

  const sums = data?.sums;
  const footerTotals = sums
    ? { totalIlsAgorot: shekels(sums.totalIlsAgorot), paidAgorot: shekels(sums.paidAgorot), balanceAgorot: shekels(sums.balanceAgorot), vatAgorot: shekels(sums.vatAgorot) }
    : undefined;
  const filtering = Boolean(query.search || Object.keys(query.filters).length || period.from || period.to || Object.keys(link).length);

  const extraChips = [
    period.from || period.to ? { key: "period", label: `תקופה: ${periodText(period)}`, icon: "calendar", tone: "primary" as const, onRemove: () => changePeriod({ from: "", to: "" }) } : null,
    Object.keys(link).length ? { key: "link", label: "סינון מקישור", icon: "link", tone: "neutral" as const, onRemove: () => setLink({}) } : null,
  ].filter((x): x is NonNullable<typeof x> => Boolean(x));

  const cancelIds = async (ids: string[]) => {
    const ok = await ckConfirm({
      title: "ביטול עסקאות",
      message: `לבטל ${fmtNum(ids.length)} עסקאות? עסקה מבוטלת יוצאת מהסכומים, מהיתרות ומהעמלות. אפשר לשחזר אותה מכרטיס העסקה.`,
      confirmLabel: "ביטול העסקאות",
      danger: true,
    });
    if (!ok) return false;
    const r = await runEach(ids, (x) => salesApi.deals.cancel(x));
    if (r.failed) toast.error(`${fmtNum(r.ok)} בוטלו · ${fmtNum(r.failed)} נכשלו`);
    else toast.success(`${fmtNum(r.ok)} עסקאות בוטלו`);
    afterBulk();
    return true;
  };
  const removeDeal = async (r: DealDto) => {
    const ok = await ckConfirm({ title: "מחיקת עסקה", message: `למחוק את העסקה «${r.productName}» של ${r.customerName || "הלקוח"}? יימחקו גם התשלומים שלה.`, confirmLabel: "מחיקה", danger: true });
    if (!ok) return;
    try {
      await salesApi.deals.remove(r.id);
      toast.success("העסקה נמחקה");
      if (id === r.id) closeDeal();
      reload();
    } catch (e) {
      toast.error(errorMessage(e, "המחיקה נכשלה"));
    }
  };

  const rowMenu = (r: DealDto): CkMenuItem[] => {
    const items: CkMenuItem[] = [{ key: "open", label: "פתיחת כרטיס", icon: "expand", kbd: "Enter", onClick: () => openDeal(r) }];
    if (canEdit && r.balanceAgorot > 0) items.push({ key: "pay", label: "רישום תשלום", icon: "coin", onClick: () => setPaying(r) });
    if (r.customerPhone) {
      items.push({
        key: "copy",
        label: "העתקת טלפון",
        icon: "copy",
        onClick: () => void copyText(r.customerPhone ?? "").then((ok) => (ok ? toast("הועתק") : toast.error("ההעתקה נחסמה בדפדפן"))),
      });
    }
    if (canEdit) {
      items.push(
        { divider: true },
        r.status === "cancelled"
          ? {
              key: "restore",
              label: "שחזור עסקה",
              icon: "undo",
              onClick: () =>
                void salesApi.deals
                  .restore(r.id)
                  .then(() => {
                    toast.success("העסקה שוחזרה");
                    reload();
                  })
                  .catch((e) => toast.error(errorMessage(e, "השחזור נכשל"))),
            }
          : { key: "cancel", label: "ביטול עסקה", icon: "x", onClick: () => void cancelIds([r.id]) },
      );
    }
    if (canManage) items.push({ key: "delete", label: "מחיקת עסקה", icon: "trash", danger: true, onClick: () => void removeDeal(r) });
    return items;
  };

  const today = todayIso();
  const filename = `${collections ? "גבייה" : "עסקאות"}-${period.from || period.to ? `${period.from}-${period.to}` : today}`;

  return (
    <div className="sl-page">
      <ScreenHeader
        sub={collections ? "עסקאות שנשארה בהן יתרה לתשלום — מהגדולה לקטנה" : "כל העסקאות — עם סכומים, תשלומים ויתרות"}
        primary={canEdit ? { label: "עסקה חדשה", icon: "plus", onClick: () => setCreating(true) } : undefined}
      />
      <PeriodBar value={period} onChange={changePeriod} label={collections ? "תאריך עסקה" : "תקופה"} />
      {table.error && data ? <Banner tone="danger">{table.error}</Banner> : null}
      <StatGrid min={180}>
        <StatTile icon="coin" tone="primary" loading={!data} label={filtering ? "סה״כ עסקאות (מסונן)" : "סה״כ עסקאות"} value={sums ? fmtIls(sums.totalIlsAgorot) : undefined} sub={data ? `${fmtNum(data.total)} עסקאות` : undefined} />
        <StatTile icon="check" tone="success" loading={!data} label="שולם" value={sums ? fmtIls(sums.paidAgorot) : undefined} />
        <StatTile icon="clock" tone="danger" warn={Boolean(sums?.balanceAgorot && sums.balanceAgorot > 0)} loading={!data} label="יתרה לגבייה" value={sums ? fmtIls(sums.balanceAgorot) : undefined} />
        <StatTile icon="file" tone="neutral" loading={!data} label="מתוכו מע״מ" value={sums ? fmtIls(sums.vatAgorot) : undefined} />
      </StatGrid>
      <DataTable<DealDto>
        mode="server"
        columns={columns}
        rows={table.rows}
        total={table.total}
        loading={table.loading}
        error={data ? null : table.error}
        onRetry={reload}
        onRefresh={reload}
        query={query}
        onQueryChange={setQuery}
        storageKey={collections ? "tb.sales.collections" : "tb.sales.deals"}
        layoutDefaults={{ hidden: hiddenByDefault, pinned: ["customerName"] }}
        colSets={colSets}
        getValueOptions={table.getValueOptions}
        searchPlaceholder="חיפוש לקוח, טלפון, מוצר, סכום…"
        extraChips={extraChips}
        onClearAll={() => {
          changePeriod({ from: "", to: "" });
          setLink({});
        }}
        builtinViews={views}
        getExtraViewState={() => periodState}
        onApplyView={(x) => {
          const v = (x ?? {}) as Partial<Period>;
          if ((v.from ?? "") !== period.from || (v.to ?? "") !== period.to) setPeriod({ from: v.from ?? "", to: v.to ?? "" });
        }}
        footerTotals={footerTotals}
        activeRowId={id ?? null}
        onRowClick={(r) => openDeal(r)}
        rowActions={(r) =>
          collections && canEdit && r.balanceAgorot > 0
            ? [{ key: "pay", icon: "coin", label: "רישום תשלום", onClick: () => setPaying(r) }]
            : [{ key: "open", icon: "expand", label: "פתיחת כרטיס", onClick: () => openDeal(r) }]
        }
        rowMenu={rowMenu}
        bulkActions={
          canEdit
            ? [
                { key: "rep", label: "שיוך לנציג", icon: "user", onClick: async (sel: CkSelection) => setAssigning(await table.resolveIds(sel)) },
                { key: "cancel", label: "ביטול עסקאות", icon: "x", danger: true, onClick: async (sel: CkSelection) => void (await cancelIds(await table.resolveIds(sel))) },
              ]
            : []
        }
        clearSelectionKey={clearKey}
        exportFilename={filename}
        exportNote="סכומים בשקלים כמספרים. כשיש בחירה — רק הנבחרות; אחרת כל התוצאות של הסינון."
        onExport={async ({ format, columns: cols, selection }) => {
          const list = await table.resolveRows(selection);
          await exportRows(list, cols, { format, filename });
          toast.success(`יוצאו ${fmtNum(list.length)} שורות`);
        }}
        emptyTitle={collections ? "אין יתרות פתוחות" : filtering ? "אין עסקאות שמתאימות לסינון" : "עדיין אין עסקאות"}
        emptyText={collections ? "כל העסקאות בסינון שולמו במלואן." : filtering ? "נסו תקופה אחרת או נקו את הסינון." : "«עסקה חדשה» למעלה, או סגירת עסקה מכרטיס ליד."}
        renderMobileRow={(r) => (
          <>
            <b>
              {r.customerName || "—"} · {fmtIls(r.totalIlsAgorot)}
            </b>
            <small>{[r.dealOn.split("-").reverse().join("/"), r.productName, r.balanceAgorot > 0 ? `יתרה ${fmtIls(r.balanceAgorot)}` : DEAL_STATUS[r.status]?.label].filter(Boolean).join(" · ")}</small>
          </>
        )}
      />

      {id ? <DealSheet key={id} id={id} onClose={closeDeal} onChanged={reload} /> : null}
      <DealDialog open={creating} onClose={() => setCreating(false)} onSaved={saved} />
      <PaymentDialog
        open={Boolean(paying)}
        deal={paying}
        onClose={() => setPaying(null)}
        onSaved={() => {
          toast.success("היתרה עודכנה");
          reload();
        }}
      />
      <AssignRepDialog ids={assigning} onClose={() => setAssigning(null)} onDone={afterBulk} />
    </div>
  );
}

export default DealsPage;
